<template>
  <div>
    <app-advanced-table-section
      :title="title"
      :headers="computedCoveragesEditingHeaders"
      id="coverages"
      :items="computedCoveragesWithEditing"
      :clickable="true"
      :loading="loadingCoverages"
      sortBy="orderInService"
    >
      <template
        #items="{ item, visibleHeaders, getCellValue }"

      >
        <tr>
          <td
            v-for="(header, index) in visibleHeaders"
            :key="index"
            @click="cellClicked(item)"
          >
            <template v-if="header.value === 'editing'">
              <font-awesome-icon
                v-if="getCellValue(item, header)"
                :icon="['fa', 'check']" aria-label="check"
              ></font-awesome-icon>
              <div v-else></div>
            </template>
            <template v-else-if="header.value === 'void'">
              <a v-if="!isFirstCovearge(item)" @click.stop="promptVoidCoverage(item)">Void</a>
            </template>
            <template v-else>
              {{ getCellValue(item, header) }}
            </template>
          </td>
        </tr>
        <tr v-if="compareCoverages(item, coverageToVoid)">
          <td :colspan="visibleHeaders.length">
            <div >
              <div class="announcements _mbxs _mtxs">
                <div class="announcement -warning">
                  <p>Void the coverage above and any following coverages?</p>
                  <div class="anncoucement-button _flex">
                    <app-placeholder-input
                      class="_c-text-primary _b1" v-if="$isInternalUser"
                      v-model="$v.fileBoundId.$model"
                      :required="$isInternalUser"
                      :errorMessage="fileBoundIdError"
                      :invalid="$v.fileBoundId.$invalid"
                      valueKey="fileBoundId"
                      label=" Filebound ID:"
                      />
                    <button class="_mlxs btn --sm" @click="coverageToVoid = {}">Cancel</button>
                    <async-button
                      class="_mlxs btn --sm white"
                      @clicked="voidCoverage"
                      :disabled='$v.$invalid'
                      :executing="voidingCoverage"
                    >
                      <template #buttonContent>
                        <span>
                          Void
                        </span>
                      </template>
                    </async-button>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      </template>
      <template #actions v-if="showAddLink">
        <router-link
          :to="{
            name: 'coverage-add',
            params: { memberCk: memberCk }
          }"
          class="btn white --sm"
          >Add Coverage</router-link
        >
      </template>
    </app-advanced-table-section>
  </div>
</template>

<script>
  import AppAdvancedTableSection from '@sections/AdvancedTableSection.vue'
  import AppPlaceholderInput from '@atoms/PlaceholderInput'
  import AsyncButton from '@molecules/AsyncButton'
  import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

  import CoverageHelper from '@mixins/CoverageHelper'
  import validationMessages from '@mixins/validationMessages'
  import MemberEditApi from '@api/MemberEditApi'

  import { requiredIf, maxLength } from 'vuelidate/lib/validators'
  import { fileBoundIdValidation } from '@/utils'

  export default {
    mixins: [CoverageHelper, validationMessages],
    components: {
      AppAdvancedTableSection,
      AppPlaceholderInput,
      FontAwesomeIcon,
      AsyncButton
    },
    props: {
      title: {
        type: String,
        default: 'Coverage History'
      },
      coverageBeingEdited: {
        type: Object,
        default() {
          return {}
        }
      },
      showAddLink: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      memberCk() {
        return this.$store.getters['hiddenRouteData/getHiddenRouteData'].memberCk
      },
      computedCoveragesEditingHeaders () {
        let headers = [
          ...this.computedCoverageHeaders,
          { text: 'Product ID', value: 'productId', visible: false },
          { text: 'Class ID', value: 'classId', visible: false },
          { text: 'Plan ID', value: 'planId', visible: false },
          { text: '', value: 'void', visible: true, sortable: false },
          { text: 'Order In Service', value: 'orderInService', sortable: true, visible: false }
        ]

        if (!(Object.keys(this.coverageBeingEdited).length === 0)) { headers.unshift({ text: 'Editing', value: 'editing', sortable: false }) }

        return headers
      },

      computedCoveragesWithEditing() {
        return this.computedCoverages.map(c => ({
          ...c,

          ...{
          editing: this.compareCoverages(c, this.coverageBeingEdited)
        }
        }))
      },
      computedPromptVoidCoverage() {
        return Object.keys(this.coverageToVoid).length !== 0
      }
    },
    data() {
      return {
        coverageToVoid: {},
        fileBoundId: '',
        voidingCoverage: false
      }
    },
    methods: {
      cellClicked(e) {
        this.$router.push({
          name: 'coverage-edit',
          params: {
            memberCk: this.memberCk,
            subGroupCk: e.subGroupCk,
            classId: e.classId,
            planId: e.planId,
          },
          query: {
            productId: e.productId,
            effectiveDate: e.effectiveDate,
            termDate: e.termDate
          }
        })
      },
      promptVoidCoverage(coverage) {
        this.coverageToVoid = coverage
      },
      voidCoverage() {
        const body = {
          effectiveDate: this.toIso8601Date(this.coverageToVoid.effectiveDate),
          terminationDate: this.toIso8601Date(this.coverageToVoid.effectiveDate),
          groupCk: this.coverageToVoid.groupCk,
          subGroupCk: this.coverageToVoid.subGroupCk,
          planId: this.coverageToVoid.planId,
          classId: this.coverageToVoid.classId,
          subscriberCk: `${this.computedMember.subscriberCk}`,
          memberCk: `${this.computedMember.memberCk}`,
          memberType: this.computedMember.memberType,
          fileBoundId: this.fileBoundId,
          voidMember: false
        }

        this.voidingCoverage = true
        new MemberEditApi({
          simulateExample: 'Void Member - Dependent.'
        })
          .voidMember({
            body: body
          })
          .then(resp => {
            this.$store.commit('SET_ANNOUNCEMENT', 'Coverage voided')
            this.$router.push({ name: 'member-details', params: { memberCk: this.memberCk } })
          })
          .catch(err => {
            console.log(err)
          })
          .finally(() => {
            this.voidingCoverage = false
          })
      }
    },
    validations: {
      fileBoundId: {
        required: requiredIf(function() {
          return this.isInternalUser
        }),
        maxLength: maxLength(15),
        fileBoundIdValidation
      }
    }
  }
</script>
